import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  OverTheCounterOptionsDetails,
  OverTheCounterOptionsGeneral,
} from '../../sj-orm/models/assets/categories';
import {
  CounterpartyType,
  OtcOptionsAssetType,
  OverTheCounterOptionsType,
  StrategyBasicPositionType,
} from '../../sj-orm/models/assets/enums';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type OverTheCounterOptions = OverTheCounterOptionsFormGeneralType &
  OverTheCounterOptionsFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const OverTheCounterOptionsList = [
  {
    label: 'forms_assets:otcOptions.enums.type.call',
    key: OverTheCounterOptionsType.call,
  },
  {
    label: 'forms_assets:otcOptions.enums.type.put',
    key: OverTheCounterOptionsType.put,
  },
];

const CounterpartyList = [
  {
    label: 'forms_assets:otcOptions.enums.counterparty.legalEntity',
    key: CounterpartyType.legalEntity,
  },
  {
    label: 'forms_assets:otcOptions.enums.counterparty.person',
    key: CounterpartyType.person,
  },
];

const StrategyBasicPositionList = [
  {
    label: 'forms_assets:otcOptions.enums.strategy.buyingCall',
    key: StrategyBasicPositionType.buyingCall,
  },
  {
    label: 'forms_assets:otcOptions.enums.strategy.sellingCall',
    key: StrategyBasicPositionType.sellingCall,
  },
  {
    label: 'forms_assets:otcOptions.enums.strategy.buyingPut',
    key: StrategyBasicPositionType.buyingPut,
  },
  {
    label: 'forms_assets:otcOptions.enums.strategy.sellingPut',
    key: StrategyBasicPositionType.sellingPut,
  },
];

export const OtcOptionsBasicAssetList = [
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.stocks',
    key: OtcOptionsAssetType.stocks,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.commodity',
    key: OtcOptionsAssetType.commodity,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.currency',
    key: OtcOptionsAssetType.currency,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.cryptocurrency',
    key: OtcOptionsAssetType.cryptocurrency,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.bonds',
    key: OtcOptionsAssetType.bonds,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.index',
    key: OtcOptionsAssetType.index,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.interestRate',
    key: OtcOptionsAssetType.interestRate,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.futuresContract',
    key: OtcOptionsAssetType.futuresContract,
  },
  {
    label: 'forms_assets:otcOptions.enums.otcOptionAssetType.other',
    key: OtcOptionsAssetType.other,
  },
];

type OverTheCounterOptionsHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.TEXT;
  type: QuestionType.SELECT_FROM_LIST;
  amount: QuestionType.NUMBER;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR;
  basicAssetType: QuestionType.SELECT_FROM_LIST;
  otherBasicAssetType: QuestionType.TEXT;
  basicAssetName: QuestionType.TEXT;
  basicAssetAmount: QuestionType.NUMBER;
  basicAssetHolder: QuestionType.TEXT;
};

type OverTheCounterOptionsHelpDetails = {
  buyingPrice?: QuestionType.CURRENCY_CALCULATOR;
  strikePrice?: QuestionType.CURRENCY_CALCULATOR;
  counterpartyType: QuestionType.SELECT_FROM_LIST;
  legalEntityName?: QuestionType.TEXT;
  personFullName?: QuestionType.TEXT;
  optionPeriod?: QuestionType.DATE_TIME;
  dealReserves?: QuestionType.CHECKBOX;
  dealReservesData?: QuestionType.TEXT;
  strategyBasicPosition?: QuestionType.SELECT_FROM_LIST;
  strategyDescription?: QuestionType.TEXT_MULTILINE;
  attachments?: QuestionType.FILE_UPLOAD;
};

type OverTheCounterOptionsFormGeneralType = TDtoWrapper<
  OverTheCounterOptionsGeneral,
  OverTheCounterOptionsHelpGeneral
>;

type OverTheCounterOptionsFormDetailsType = TDtoWrapper<
  OverTheCounterOptionsDetails,
  OverTheCounterOptionsHelpDetails
>;

const OverTheCounterOptionsFormGeneral: OverTheCounterOptionsFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:otcOptions.inputs.type.label',
    tooltip: 'forms_assets:otcOptions.inputs.type.tooltip',
    value: OverTheCounterOptionsType.put,
    items: OverTheCounterOptionsList,
    required: true,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:otcOptions.inputs.amount.label',
    required: true,
    value: 0,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:otcOptions.inputs.currentMarketValue.label',
    tooltip: 'forms_assets:otcOptions.inputs.currentMarketValue.tooltip',
    equivalentValueTooltip:
      // eslint-disable-next-line sonarjs/no-duplicate-string
      'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  basicAssetType: {
    items: OtcOptionsBasicAssetList,
    label: 'forms_assets:otcOptions.inputs.basicAssetType.label',
    type: QuestionType.SELECT_FROM_LIST,
  },
  otherBasicAssetType: {
    label: 'forms_assets:otcOptions.inputs.otherBasicAssetType.label',
    type: QuestionType.TEXT,
    showIf: { basicAssetType: { value: OtcOptionsAssetType.other } },
  },
  basicAssetName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.basicAssetName.label',
    tooltip: 'forms_assets:otcOptions.inputs.basicAssetName.tooltip',
    value: '',
    // required: true,
  },
  basicAssetAmount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:otcOptions.inputs.basicAssetAmount.label',
    value: 0,
    // required: true,
  },
  basicAssetHolder: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.basicAssetHolder.label',
    value: '',
    // required: true,
  },
};

const OverTheCounterOptionsFormDetails: OverTheCounterOptionsFormDetailsType = {
  buyingPrice: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:otcOptions.inputs.buyingPrice.label',
    tooltip: 'forms_assets:otcOptions.inputs.buyingPrice.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    order: 1,
  },
  strikePrice: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:otcOptions.inputs.strikePrice.label',
    tooltip: 'forms_assets:otcOptions.inputs.strikePrice.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    order: 2,
  },
  counterpartyType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:otcOptions.inputs.counterpartyType.label',
    tooltip: 'forms_assets:otcOptions.inputs.counterpartyType.tooltip',
    order: 3,
    items: CounterpartyList,
  },
  legalEntityName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.legalEntityName.label',
    order: 4,
    showIf: {
      counterpartyType: {
        value: CounterpartyType.legalEntity,
      },
    },
  },
  personFullName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.personFullName.label',
    order: 5,
    showIf: {
      counterpartyType: {
        value: CounterpartyType.person,
      },
    },
  },
  optionPeriod: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:otcOptions.inputs.optionPeriod.label',
    tooltip: 'forms_assets:otcOptions.inputs.optionPeriod.tooltip',
    order: 6,
  },
  dealReserves: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:otcOptions.inputs.dealReserves.label',
    tooltip: 'forms_assets:otcOptions.inputs.dealReserves.tooltip',
    order: 8,
  },
  dealReservesData: {
    type: QuestionType.TEXT,
    label: 'forms_assets:exchangeOptions.inputs.dealReservesData.label',
    order: 9,
    showIf: {
      dealReserves: {
        value: true,
      },
    },
  },
  strategyBasicPosition: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:otcOptions.inputs.strategyBasicPosition.label',
    tooltip: 'forms_assets:otcOptions.inputs.strategyBasicPosition.tooltip',
    order: 10,
    items: StrategyBasicPositionList,
  },
  strategyDescription: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:otcOptions.inputs.strategyDescription.label',
    order: 11,
  },
  attachments: baseAssetFields.attachments,
};

const OverTheCounterOptionsForm = [
  OverTheCounterOptionsFormGeneral,
  OverTheCounterOptionsFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder(['buyingPrice', 'strikePrice']),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { OverTheCounterOptionsForm };
